import { Component, Prop, State, Element, Build, Host, h } from '@stencil/core';
import { isProduction, getAuthUrl, getDomain, removeCookie, parseJwt, trackEvent, openRegistrationPopup, analytics } from '../../social-auth/shared/index';

const COMPONENT_NAME = 'sn-google-one-tap-type-2';

/**
 * The frame that hosts Google One Tap on the registered origin. Served by SignNowMain
 * (`routes/frontend.php` → `GoogleOneTapController@intermediate`).
 *
 * There is one frame per environment and each one hard-locks its parents: the production frame is
 * initialised with `allowed_parent_origin: 'https://*.signnow.com'` and the RC frame with
 * `'https://*.signnow-rc.xyz'`. A page therefore has to embed the frame of its own environment —
 * pointing an RC page at the production frame makes Google reject the parent — and each frame also
 * picks the matching Google client id by itself.
 */
const FRAME_PATH = '/frame/intermediate-google-one-tap';
const FRAME_ORIGIN_PROD = 'https://www.signnow.com';
const FRAME_ORIGIN_RC = 'https://www.signnow-rc.xyz';

/**
 * Message types the SignNowMain frame posts itself. Verified against the live frame — it sends
 * exactly these three, each stamped with `isGoogleOneTapMessage: true`.
 */
const MESSAGE_CREDENTIAL = 'handleGoogleOneTapCb';
const MESSAGE_DISPLAYED = 'displayed';
const MESSAGE_CANCELED = 'canceled';

/**
 * Commands of GSI's own intermediate-iframe protocol, which travel alongside the frame's messages
 * and are marked with `sentinel: 'onetap_google'` instead. Read out of the `gsi/client` bundle:
 * the library posts `intermediate_iframe_ready` and blocks the prompt until the parent answers
 * `parent_frame_ready` with the same nonce from an origin matching `allowed_parent_origin`.
 */
const GSI_SENTINEL = 'onetap_google';
const GSI_IFRAME_READY = 'intermediate_iframe_ready';
const GSI_PARENT_READY = 'parent_frame_ready';
const GSI_IFRAME_RESIZE = 'intermediate_iframe_resize';
const GSI_IFRAME_CLOSE = 'intermediate_iframe_close';
const GSI_IFRAME_DONE = 'intermediate_iframe_done';

/**
 * Google One Tap for SignNow **forms pages**, driven by an iframe instead of the GSI SDK.
 *
 * Google only prompts on origins registered as authorized JavaScript origins for the client id.
 * A template served from both the apex domain and arbitrary subdomains cannot register them all,
 * so the prompt is rendered inside an iframe hosted on the one registered origin, which posts the
 * credential out to this component. This is the mechanism of the legacy
 * `scripts/one-tap-iframe.js` (`.google-one-tap-subdomain-forms`), and it works on every URL —
 * apex and subdomains alike — because the origin Google sees is always the iframe's.
 *
 * After a successful session exchange it does NOT redirect into the product: it opens
 * `sn-popup-type-1`, so the user can return to editing the form they came for. Requires
 * `sn-popup-type-1` on the same page.
 *
 * GSI's intermediate-iframe mode expects the embedding page to hold up the parent half of a
 * handshake — see `handleGsiCommand`. The library ships only the iframe half (in AMP it is
 * `amp-onetap-google` that answers), so without the reply below the prompt never appears at all.
 *
 * Messages are accepted only from the iframe's own origin (derived from `iframe-src`), or from
 * `trusted-origins` when the page embeds the iframe itself. The legacy script accepted a
 * credential from any window that could reach it; that credential goes straight into a session
 * POST, so it is validated here.
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @example
 *   <sn-google-one-tap-type-2 iframe-src="https://www.signnow.com/one-tap-frame"></sn-google-one-tap-type-2>
 */
@Component({
  tag: 'sn-google-one-tap-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNGoogleOneTapType2 {
  @Element() host!: HTMLElement;

  /** Color mode for theming. */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * URL of the frame that runs Google One Tap and posts the credential back. Left empty it is
   * resolved per environment — the production frame on signnow.com, the RC frame on everything
   * else — which is what each frame's `allowed_parent_origin` requires. Set it only to pin a
   * specific frame.
   */
  @Prop({ attribute: 'iframe-src' }) iframeSrc?: string;

  /** Frame width — must fit the One Tap prompt the frame may draw inside itself. */
  @Prop({ attribute: 'iframe-width' }) iframeWidth: string = '391px';

  /** Frame height — must fit the One Tap prompt the frame may draw inside itself. */
  @Prop({ attribute: 'iframe-height' }) iframeHeight: string = '500px';

  /**
   * Comma-separated origins allowed to post the credential, in addition to the `iframe-src`
   * origin. Only needed when the page embeds the iframe itself.
   */
  @Prop({ attribute: 'trusted-origins' }) trustedOrigins?: string;

  /**
   * Form id handed to `sn-popup-type-1` so its "Back to form editing" action can resolve the
   * editor URL. When empty it is read from a `[data-formid]` element on the page.
   */
  @Prop({ attribute: 'form-id' }) formId?: string;

  /**
   * Whether the frame is currently showing a prompt the user must be able to click.
   *
   * The frame keeps its size at all times — GSI will not prompt inside a frame that is collapsed or
   * `visibility: hidden`, and since the prompt is what produces the `displayed` message, sizing the
   * frame only after `displayed` deadlocks: no size, no prompt, no message, no size.
   *
   * So instead of the size, this toggles hit-testing. Until the frame says it drew something the
   * frame is click-through, which is what matters in the common case: with
   * `use_fedcm_for_prompt: true` the prompt is browser UI and the frame's own document stays empty,
   * so a hit-testable box would be an invisible overlay stealing clicks from the page.
   */
  @State() promptInteractive: boolean = false;

  /**
   * Height the frame asked for through `intermediate_iframe_resize`, which is the prompt's real
   * height. Overrides `iframe-height` while a prompt is up; cleared when the frame closes.
   */
  @State() frameHeight?: string;

  private pageUrl: string = '';
  private messageHandler?: (event: MessageEvent) => void;

  componentWillLoad() {
    if (Build.isBrowser) {
      this.pageUrl = window.location.href;
    }
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;
    if (window.__ME_GLOBALS__?.isBot) return;

    if (!this.allowedOrigins().length) {
      console.warn(`[${COMPONENT_NAME}] no iframe-src and no trusted-origins — One Tap cannot be received.`);
      return;
    }

    this.messageHandler = this.handleMessage.bind(this);
    window.addEventListener('message', this.messageHandler, false);
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = undefined;
    }
  }

  /** The frame to embed: an explicit `iframe-src`, otherwise the one for this environment. */
  private resolveFrameSrc(): string {
    if (this.iframeSrc !== undefined) return this.iframeSrc;
    return `${isProduction() ? FRAME_ORIGIN_PROD : FRAME_ORIGIN_RC}${FRAME_PATH}`;
  }

  /** Origins the credential may come from: the iframe's own origin plus any explicitly trusted. */
  private allowedOrigins(): string[] {
    const origins: string[] = [];
    const src = this.resolveFrameSrc();

    if (src) {
      try {
        origins.push(new URL(src, window.location.href).origin);
      } catch {
        console.warn(`[${COMPONENT_NAME}] iframe-src is not a valid URL:`, src);
      }
    }

    if (this.trustedOrigins) {
      origins.push(
        ...this.trustedOrigins
          .split(',')
          .map(o => o.trim())
          .filter(Boolean),
      );
    }

    return origins;
  }

  private handleMessage(event: MessageEvent) {
    if (!this.allowedOrigins().includes(event.origin)) return;

    const data = (event.data || {}) as {
      isGoogleOneTapMessage?: boolean;
      sentinel?: string;
      command?: string;
      nonce?: string;
      height?: number;
      type?: string;
      credential?: string;
    };

    // GSI's own protocol comes marked with a sentinel rather than the frame's flag.
    if (data.sentinel === GSI_SENTINEL) {
      this.handleGsiCommand(data, event);
      return;
    }

    // The frame stamps every message with this flag; it keeps unrelated traffic from the same
    // origin (analytics, chat widgets) out of the switch below.
    if (!data.isGoogleOneTapMessage) return;

    // Debug-level so the parent console shows the handshake: silence here means the frame never
    // reported anything, which points at the frame or at Google, not at this component.
    console.debug(`[${COMPONENT_NAME}] frame message:`, data.type);

    switch (data.type) {
      case MESSAGE_CREDENTIAL:
        if (!data.credential) {
          console.error(`[${COMPONENT_NAME}] empty credential in ${MESSAGE_CREDENTIAL}`);
          break;
        }
        this.handleCredentialResponse(data.credential);
        break;
      case MESSAGE_DISPLAYED:
        // Something is on screen inside the frame — let it receive clicks.
        this.promptInteractive = true;
        break;
      case MESSAGE_CANCELED:
        // Not displayed, dismissed, auto-closed or timed out — stop intercepting clicks.
        this.promptInteractive = false;
        break;
      default:
    }
  }

  /**
   * The parent half of GSI's intermediate-iframe protocol.
   *
   * `intermediate_iframe_ready` must be answered with `parent_frame_ready` carrying the same nonce.
   * GSI then checks that the reply came from `window.parent` and that our origin matches the frame's
   * `allowed_parent_origin`, and only then unblocks the prompt; an unanswered handshake is what
   * produces "A previous attempt has been made to verify the parent origin and is still being
   * processed" in the frame's console and no prompt whatsoever.
   *
   * The frame also asks the parent to size and close it, which is how the pre-FedCM prompt gets its
   * real height instead of the `iframe-height` guess.
   */
  private handleGsiCommand(data: { command?: string; nonce?: string; height?: number }, event: MessageEvent) {
    console.debug(`[${COMPONENT_NAME}] gsi command:`, data.command);

    switch (data.command) {
      case GSI_IFRAME_READY: {
        const frame = this.host.querySelector('iframe');
        // Reply to the frame itself, addressed to its own origin — never '*'.
        frame?.contentWindow?.postMessage({ sentinel: GSI_SENTINEL, command: GSI_PARENT_READY, nonce: data.nonce, parentMode: 'client' }, event.origin);
        break;
      }
      case GSI_IFRAME_RESIZE:
        // The frame knows how tall its prompt is; trust it over the configured height.
        if (typeof data.height === 'number' && data.height > 0) {
          this.frameHeight = `${data.height}px`;
          this.promptInteractive = true;
        }
        break;
      case GSI_IFRAME_CLOSE:
      case GSI_IFRAME_DONE:
        this.frameHeight = undefined;
        this.promptInteractive = false;
        break;
      default:
    }
  }

  private async handleCredentialResponse(credentialToken: string) {
    // The prompt is done with — stop intercepting clicks before anything slower runs.
    this.promptInteractive = false;

    await trackEvent('/api/v2/metrics/google-one-tap-forms-click-me', {
      login_status: true,
      page_url: this.pageUrl,
    });

    // The legacy module opens the modal here, before the session POST resolves, so the user sees
    // feedback immediately. The popup's own action only needs the form id.
    openRegistrationPopup(this.formId);

    removeCookie('XSRF-TOKEN', { domain: getDomain(), path: '/' });

    const credential = parseJwt(credentialToken);

    try {
      const response = await fetch(getAuthUrl('/auth/v1/sessions/social'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token: credentialToken,
          provider: 'google',
          login_source: 'pdffiller_one_tap',
        }),
      });

      const result = await response.json();
      const credentialEmail = credential.email;

      if (result.social_reg) {
        analytics.registration();
        analytics.trialPurchased();
        analytics.freeTrialAction('Google One Tap', 'Sign-up');
        analytics.ceSignUp(credentialEmail);
        await trackEvent('/api/v2/metrics/google-one-tap-login-forms-event-me', {
          login_status: true,
          page_url: this.pageUrl,
          auth_type: 'Sign-up',
        });
      } else if (result.social_reg === false) {
        analytics.freeTrialAction('Google One Tap', 'Login');
        analytics.ceLogin(credentialEmail);
        await trackEvent('/api/v2/metrics/google-one-tap-login-forms-event-me', {
          login_status: true,
          page_url: this.pageUrl,
          auth_type: 'Login',
        });
      }
      // No redirect — the popup is the end of this flow.
    } catch (error) {
      console.error('Google One Tap authentication error:', error);
      await trackEvent('/api/v2/metrics/google-one-tap-forms-fail-me', {
        login_status: false,
        page_url: this.pageUrl,
      });
    }
  }

  render() {
    const src = Build.isBrowser ? this.resolveFrameSrc() : '';

    // Nothing on the server (the environment is resolved from the hostname at runtime), and
    // nothing in listener-only mode, where the page embeds the frame itself.
    if (!src) return <Host data-color-mode={this.colorMode}></Host>;

    return (
      <Host data-color-mode={this.colorMode}>
        <iframe
          class={`${COMPONENT_NAME}__frame`}
          src={src}
          title="Google One Tap"
          // Delegates the FedCM permission into the frame; without it `navigator.credentials.get`
          // is blocked in a cross-origin iframe and GSI cannot show the browser-native prompt.
          allow="identity-credentials-get"
          // Always rendered at full size — GSI does not prompt inside a collapsed or hidden frame.
          // Click-through until the frame reports a prompt, so an empty frame (the FedCM case, where
          // the prompt is browser UI) cannot steal clicks from the page underneath.
          style={{
            width: this.iframeWidth,
            height: this.frameHeight || this.iframeHeight,
            pointerEvents: this.promptInteractive ? 'auto' : 'none',
          }}
        ></iframe>
      </Host>
    );
  }
}
